import React from 'react'
import { Box, Tab, Tabs } from '@mui/material'

import useRouter from '../../hooks/useRouter'
import useSkyProducts from '../../hooks/socialdash/useSkyProducts'
import { ADMIN_SLUG } from './productTabsModel'

/** The views under the Admin tab. */
export type AdminView = 'post-data' | 'influencer-data' | 'reconciliation'

export const ADMIN_VIEW_ROUTES: Record<AdminView, string> = {
  'post-data': 'sky-social-media-entities',
  'influencer-data': 'sky-social-media-influencer-data',
  reconciliation: 'sky-social-media-reconciliation',
}

/**
 * The second row under Admin: Post data (correct one post's numbers) and
 * Influencer data (upload Sky's influencer export, #1694), then Reconciliation
 * (Kai, 3 Nov 2026). Each page draws it, so the Admin tab stays one tab and
 * every view is one click from the others.
 *
 * Reconciliation is open to every Sky viewer, not only admins, so on that page
 * the row draws only for an admin (the `all` card is the evidence, as in
 * productTabsModel); anyone else reaches it from the top row instead.
 */
export const AdminViewTabs: React.FC<{ view: AdminView }> = ({ view }) => {
  const router = useRouter()
  const { products, isLoading } = useSkyProducts()
  const isAdmin = products.some((p) => p.slug === ADMIN_SLUG)
  if (view === 'reconciliation' && (isLoading || !isAdmin)) return null
  return (
    <Box sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
      <Tabs
        value={view}
        onChange={(_, next: AdminView) => router.navigate(ADMIN_VIEW_ROUTES[next])}
        sx={{
          minHeight: 40,
          '& .MuiTab-root': { minHeight: 40, textTransform: 'none' },
          '& .MuiTabs-indicator': { bgcolor: 'info.main' },
          '& .MuiTab-root.Mui-selected': { color: 'info.main' },
        }}
        data-testid="sky-admin-views"
      >
        <Tab value="post-data" label="Post data" data-testid="sky-admin-view-post-data" />
        <Tab value="influencer-data" label="Influencer data" data-testid="sky-admin-view-influencer-data" />
        <Tab value="reconciliation" label="Reconciliation" data-testid="sky-admin-view-reconciliation" />
      </Tabs>
    </Box>
  )
}

export default AdminViewTabs
